import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { api, exigir, ErroApi } from "../api/cliente";
import type { components } from "../api/esquema";
import { Botao } from "../componentes/kit/Botao";
import { Campo } from "../componentes/kit/Campo";
import { PainelLateral } from "../componentes/kit/PainelLateral";

type Cliente = components["schemas"]["Cliente"];

/** E3-02 · Cadastro manual de processo no painel: número, cliente (existente ou novo), parte contrária, vara, fase.
 *  O tribunal sai do número (a API deduz). Ao salvar, abre a página do processo. */
export function NovoProcesso({ aoFechar }: { aoFechar: () => void }) {
  const navegar = useNavigate();
  const [numero, setNumero] = useState("");
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [novoCliente, setNovoCliente] = useState(false);
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [contraria, setContraria] = useState("");
  const [vara, setVara] = useState("");
  const [fase, setFase] = useState("");

  useEffect(() => {
    const id = window.setTimeout(() => setTermo(busca.trim()), 250);
    return () => window.clearTimeout(id);
  }, [busca]);

  const achados = useQuery({
    queryKey: ["clientes", termo],
    queryFn: () => exigir(api.GET("/api/clientes", { params: { query: { busca: termo, limite: 6 } } })),
    enabled: termo.length >= 2 && !cliente && !novoCliente,
  });

  const salvar = useMutation({
    mutationFn: async () => {
      let id = cliente?.id;
      if (!id) {
        const c = await exigir(api.POST("/api/clientes", { body: {
          nome, cpf: cpf.trim() || undefined, telefones: telefone.trim() ? [telefone.trim()] : [],
        } }));
        id = c.id;
      }
      return exigir(api.POST("/api/processos", { body: {
        numero, clientes: [{ id, papel: "autor" }], vara: vara.trim() || undefined, fase: fase.trim() || undefined,
        partes: contraria.split(",").map((s) => s.trim()).filter(Boolean).map((nome) => ({ nome, polo: "passivo" as const })),
        area: "superendividamento", segredo_justica: false,
      } }));
    },
    onSuccess: (p) => { aoFechar(); navegar({ to: "/processos/$numero", params: { numero: p.numero } }); },
  });

  function enviar(e: FormEvent) { e.preventDefault(); salvar.mutate(); }
  const podeSalvar = numero.trim() && (cliente || (novoCliente && nome.trim().length >= 2));

  return (
    <PainelLateral titulo="Novo processo" aoFechar={aoFechar}
      rodape={<><Botao onClick={aoFechar}>Cancelar</Botao>
        <Botao variante="primario" type="submit" form="novo-processo" disabled={!podeSalvar} carregando={salvar.isPending}>Cadastrar processo</Botao></>}>
      <form id="novo-processo" onSubmit={enviar} className="flex flex-col gap-4">
        <Campo rotulo="Número do processo" required value={numero} onChange={(e) => setNumero(e.target.value)}
          placeholder="0000000-00.0000.0.00.0000" ajuda="O tribunal sai do número" />

        <div className="flex flex-col gap-2">
          <span className="text-meta font-medium text-texto-3">Cliente</span>
          {cliente ? (
            <div className="flex items-center justify-between gap-3 rounded-controle bg-superficie-2 px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate font-medium">{cliente.nome}</span>
                <span className="block text-meta text-texto-3">{[cliente.cpf && `CPF ${cliente.cpf}`, `${cliente.processos.length} ${cliente.processos.length === 1 ? "processo" : "processos"}`].filter(Boolean).join(" · ")}</span>
              </span>
              <Botao variante="fantasma" onClick={() => setCliente(null)}>Trocar</Botao>
            </div>
          ) : novoCliente ? (
            <div className="flex flex-col gap-3">
              <Campo rotulo="Nome completo" required value={nome} onChange={(e) => setNome(e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <Campo rotulo="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="000.000.000-00" />
                <Campo rotulo="Telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(54) 99999-9999" />
              </div>
              <button type="button" onClick={() => setNovoCliente(false)} className="w-fit cursor-pointer text-meta text-texto-3 underline underline-offset-2">Buscar um cliente já cadastrado</button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <input className="campo" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome, CPF ou telefone" aria-label="Buscar cliente" />
              {achados.data && achados.data.length > 0 && (
                <ul className="entre-fios rounded-controle fio">
                  {achados.data.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => setCliente(c)} className="flex w-full cursor-pointer flex-col px-3 py-2 text-left hover:bg-superficie-2">
                        <span className="font-medium">{c.nome}</span>
                        <span className="text-meta text-texto-3">{[c.cpf && `CPF ${c.cpf}`, ...c.telefones].filter(Boolean).join(" · ") || "sem CPF ou telefone"}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {termo.length >= 2 && achados.data?.length === 0 && <p className="text-meta text-texto-3">Nenhum cliente com “{termo}”.</p>}
              <button type="button" onClick={() => { setNovoCliente(true); setNome(/\d/.test(busca) ? "" : busca); }} className="w-fit cursor-pointer text-meta font-medium text-texto-1 underline underline-offset-2">Cadastrar cliente novo</button>
            </div>
          )}
        </div>

        <Campo rotulo="Parte contrária" value={contraria} onChange={(e) => setContraria(e.target.value)} placeholder="Banrisul, Caixa" ajuda="Separe por vírgula" />
        <div className="grid grid-cols-2 gap-3">
          <Campo rotulo="Vara" value={vara} onChange={(e) => setVara(e.target.value)} />
          <Campo rotulo="Fase" value={fase} onChange={(e) => setFase(e.target.value)} placeholder="Inicial" />
        </div>
        {salvar.error && <p role="alert" className="text-meta text-alerta">{salvar.error instanceof ErroApi ? salvar.error.message : "Não foi possível cadastrar."}</p>}
      </form>
    </PainelLateral>
  );
}
